import React, { useState, useEffect } from 'react';
import { X, Target, Calendar, Play, User } from 'lucide-react';
import { createGoal } from '../../../services/studentGoalsService';
import { getPlays } from '../../../services/playsService';

const CreateGoalModal = ({ isOpen, onClose, onGoalCreated }) => {
  const [formData, setFormData] = useState({
    goal_type: 'overall_accuracy',
    target_value: '',
    target_date: '',
    play_id: '',
    character_name: '',
    difficulty_level: 'intermediate'
  });
  const [plays, setPlays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadPlays();
      // Set default target date to 3 months from now
      const defaultDate = new Date();
      defaultDate?.setMonth(defaultDate?.getMonth() + 3);
      setFormData(prev => ({
        ...prev,
        target_date: defaultDate?.toISOString()?.split('T')?.[0]
      }));
    }
  }, [isOpen]);

  const loadPlays = async () => {
    try {
      const data = await getPlays();
      setPlays(data);
    } catch (err) {
      console.error('Error loading plays:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // Validate form
      if (!formData?.target_value || !formData?.target_date) {
        throw new Error('Please fill in all required fields');
      }

      // Prepare goal data
      const goalData = {
        goal_type: formData?.goal_type,
        target_value: parseFloat(formData?.target_value),
        target_date: new Date(formData.target_date)?.toISOString(),
        difficulty_level: formData?.difficulty_level
      };

      // Add optional fields
      if (formData?.goal_type === 'play_specific' && formData?.play_id) {
        goalData.play_id = formData?.play_id;
      }

      if (formData?.goal_type === 'character_mastery' && formData?.character_name) {
        goalData.character_name = formData?.character_name;
      }

      await createGoal(goalData);
      onGoalCreated();
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Target className="w-8 h-8" />
              <h2 className="text-2xl font-bold">Create New Goal</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {/* Goal Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Goal Type *
            </label>
            <select
              name="goal_type"
              value={formData?.goal_type}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
              required
            >
              <option value="overall_accuracy">Overall Pronunciation Accuracy</option>
              <option value="play_specific">Play-Specific Mastery</option>
              <option value="weekly_practice">Weekly Practice Sessions</option>
              <option value="character_mastery">Character Pronunciation Mastery</option>
            </select>
          </div>

          {/* Target Value */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Target {formData?.goal_type === 'weekly_practice' ? 'Sessions' : 'Accuracy (%)'} *
            </label>
            <input
              type="number"
              name="target_value"
              value={formData?.target_value}
              onChange={handleChange}
              min="0"
              max={formData?.goal_type === 'weekly_practice' ? '100' : '100'}
              step={formData?.goal_type === 'weekly_practice' ? '1' : '0.1'}
              placeholder={formData?.goal_type === 'weekly_practice' ? 'e.g., 5' : 'e.g., 85.0'}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
              required
            />
          </div>

          {/* Target Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Target Date *
            </label>
            <input
              type="date"
              name="target_date"
              value={formData?.target_date}
              onChange={handleChange}
              min={new Date()?.toISOString()?.split('T')?.[0]}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
              required
            />
          </div>

          {/* Play Selection (for play-specific goals) */}
          {formData?.goal_type === 'play_specific' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Play className="w-4 h-4" />
                Select Play
              </label>
              <select
                name="play_id"
                value={formData?.play_id}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
              >
                <option value="">Choose a play...</option>
                {plays?.map((play) => (
                  <option key={play?.id} value={play?.id}>
                    {play?.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Character Name (for character mastery goals) */}
          {formData?.goal_type === 'character_mastery' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <User className="w-4 h-4" />
                Character Name
              </label>
              <input
                type="text"
                name="character_name"
                value={formData?.character_name}
                onChange={handleChange}
                placeholder="e.g., Romeo, Juliet, Hamlet"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
              />
            </div>
          )}

          {/* Difficulty Level */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Difficulty Level
            </label>
            <div className="grid grid-cols-3 gap-3">
              {['beginner', 'intermediate', 'advanced']?.map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, difficulty_level: level }))}
                  className={`px-4 py-3 rounded-lg font-medium transition-all ${
                    formData?.difficulty_level === level
                      ? 'bg-purple-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {level?.charAt(0)?.toUpperCase() + level?.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Goal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGoalModal;